export type SrRowSpacing = 'none' | 'minimal' | 'tight' | 'small' | 'normal' | 'airy';
export const rowSpacingSchema = {
  'type': 'string',
  'enum': ['none', 'minimal', 'tight', 'small', 'normal', 'airy'],
  'enumDescription': ['No spacing added', 'Minimal spacing', 'Tight spacing', 'Slightly less than normal spacing', 'Normal spacing', 'More than normal spacing'],
  'description': 'Vertical spacing between rows (default: normal)',
};
export const colSplitSchema = {
  'type': 'string',
  'enum': ['1/11', '2/10', '3/9', '4/8', '5/7', '6/6', '7/5', '8/4', '9/3'],
  'enumDescription': [
    'Label column is 1/12 of the width', 
    'Label column is 1/6 of the width', 
    'Label column is 1/4 of the width', 
    'Label column is 1/3 of the width (default)', 
    'Label column is 5/12 of the width', 
    'Label column is 1/2 of the width (center split)', 
    'Label column is 7/12 of the width', 
    'Label column is 2/3 of the width',
    'Label column is 3/4 of the width', 
  ],
  'description': 'Column split (when not running in full-width mode, default: 4/8)',
};

const PreFonts = ['Consolas', 'Courier New'];
export type PreFont = 'Consolas' | 'Courier New';

interface NonLangDependentTemplateDefaults {
  rowSpacing: SrRowSpacing;
  defaultColSplit: string;
  defaultPreFont: PreFont;
  reportNormalFont: string | null;
  reportH1TextSize: string | number;
  reportH2TextSize: string | number;
  reportH3TextSize: string | number;
  reportNormalTextSize: number | null;
  reportSmallTextSize: string | number;
}

interface LangDependentTemplateDefaults {
  defaultAndWord: string;
  mandatoryProhibitFieldsText: string;
  mandatoryAlertFieldsText: string;
  checkboxListOtherOptionName: string;
  checkboxListOtherOptionPlaceholderText: string | undefined;
  listSectionItemName: string;
  listSectionAddButtonText: string;
  inputNumberRefWarningText: string;
  inputNumberRangeWarningText: string;

  summaryDefaultLabel: string;
  summaryDefaultEditableLabel: string;
  summaryInconsistencyWarningText: string;
  summaryInconsistencyWarningAllowedText: string;
  summaryInconsistencyWarningReferencedText: string;
}

export const templateDefaultsSchema = {
  'type': 'object',
  'properties': {
    'defaultAndWord': { 'type': 'string', 'description': "Language dependent 'and'-word used for instance to properly display an array of values in text, e.g. \"[value1], [value2], and [value3]\"" },
    'defaultPreFont': { 'type': 'string', 'enum': PreFonts, 'enumDescription': PreFonts, 'description': 'Font used to style preformatted text sections' },
    'reportNormalFont': { 'type': ['string', 'null'], 'description': 'Text-font used for normal text (default: empty which corresponds to IDS7 default, Segoe UI)' },
    'reportNormalTextSize': { 'type': ['number', 'null'], 'description': 'Text-size used for normal text, as an absolute number (default: empty which corresponds to IDS7 default, often 9)' },
    'reportH1TextSize': { 'type': ['string', 'number'], 'description': 'Text-size used for header level one text, either as an absolute number or as a precentage (default: 150%)', 'pattern': '^[0-9]+%?$' },
    'reportH2TextSize': { 'type': ['string', 'number'], 'description': 'Text-size used for header level two text, either as an absolute number or as a precentage (default: 125%)', 'pattern': '^[0-9]+%?$' },
    'reportH3TextSize': { 'type': ['string', 'number'], 'description': 'Text-size used for header level three text, either as an absolute number or as a precentage (default: 117%)', 'pattern': '^[0-9]+%?$' },
    'reportSmallTextSize': { 'type': ['string', 'number'], 'description': 'Text-size used for small text, either as an absolute number or as a precentage (default: 85%)', 'pattern': '^[0-9]+%?$' },
    'defaultColSplit': colSplitSchema,
    'mandatoryProhibitFieldsText': { 'type': 'string', 'description': 'Text explaining that there are empty mandatory fields that prohibit form submission' },
    'mandatoryAlertFieldsText': { 'type': 'string', 'description': 'Text explaining that there are empty mandatory fields (does not prohibit form submission, just alerting the user)' },
    'checkboxListOtherOptionName': { 'type': 'string', 'description': 'Default name of the "Other" option in a checkbox list' },
    'checkboxListOtherOptionPlaceholderText': { 'type': 'string', 'description': 'Default placeholder text in the "Other" option input field of a checkbox list' },
    'listSectionItemName': { 'type': 'string', 'description': 'Name of each item in a list section, available variables are: {index}, {zindex} and {count}' }, 
    'listSectionAddButtonText': { 'type': 'string', 'description': 'Text displayed on the add new list item in a list section' }, 
    'inputNumberRefWarningText': { 'type': 'string', 'description': 'The generic warning text displayed when a input number component value is outside of the reference interval' }, 
    'inputNumberRangeWarningText': { 'type': 'string', 'description': 'The generic warning text displayed when a input number component value is outside of the allowed range (a temporary state possible during input)' }, 
    'summaryDefaultLabel': { 'type': 'string', 'description': 'Summary default label (for non-editable summaries)' },
    'summaryDefaultEditableLabel': { 'type': ['string', 'null'], 'description': 'Summary edit toggle label text for editable summaries' },
    'summaryInconsistencyWarningText': { 'type': 'string', 'description': "Summary inconsistency warning text when inconsitency isn't allowed" },
    'summaryInconsistencyWarningAllowedText': { 'type': 'string', 'description': 'Summary inconsistency warning text when inconsitency is allowed' },
    'summaryInconsistencyWarningReferencedText': { 'type': 'string', 'description': 'Summary inconsistency warning text when inconsitency is found in a referenced component' },
    'rowSpacing': rowSpacingSchema,
  },
  'description': 'Setup form defaults and default texts',
};

const baseDefaults: NonLangDependentTemplateDefaults = {
  rowSpacing: 'normal',
  defaultColSplit: '4/8', 
  defaultPreFont: 'Consolas',
  reportH1TextSize: '150%',
  reportH2TextSize: '125%',
  reportH3TextSize: '117%',
  reportNormalFont: null,
  reportNormalTextSize: null,
  reportSmallTextSize: '80%',
};

const langDependentTemplateDefaults: { [langCode: string]: LangDependentTemplateDefaults } = {
  'en': {
    defaultAndWord: ', and ',
    mandatoryProhibitFieldsText: 'The following required fields are empty:',
    mandatoryAlertFieldsText: 'The following recommended fields are empty:',
    checkboxListOtherOptionName: 'Other',
    checkboxListOtherOptionPlaceholderText: undefined,
    listSectionItemName: 'Item {index}',
    listSectionAddButtonText: 'Add new item',
    inputNumberRefWarningText: 'The given value is not within the reference interval',
    inputNumberRangeWarningText: 'The given value is not allowed',
    summaryDefaultLabel: 'Report',
    summaryDefaultEditableLabel: 'Edit report',
    summaryInconsistencyWarningText: 'Information affecting this report has been altered since entering edit mode resulting in potential inconsistencies. As a consequence, the resulting report text will revert to the auto-generated report text. Exit edit mode and re-enter to reset this state, or revert form changes leading to this state.',
    summaryInconsistencyWarningAllowedText: 'Information affecting this report has been altered since entering edit mode resulting in potential inconsistencies. These changes will no longer automatically update the report potentially leading to a mismatch between the report and underlying structured information.',
    summaryInconsistencyWarningReferencedText: 'Information affecting this report has been altered elsewere resulting in potential inconsistencies.',
  },
  'sv': {
    defaultAndWord: ', och ',
    mandatoryProhibitFieldsText: 'Följande obligatoriska fält är tomma:',
    mandatoryAlertFieldsText: 'Följande rekommenderade fält är tomma:',
    checkboxListOtherOptionName: 'Annat',
    checkboxListOtherOptionPlaceholderText: undefined,
    listSectionItemName: 'Sektion {index}',
    listSectionAddButtonText: 'Lägg till ny',
    inputNumberRefWarningText: 'Det angivna värdet ligger utanför referensintervallet',
    inputNumberRangeWarningText: 'Det angivna värdet är ej tillåtet',
    summaryDefaultLabel: 'Rapport',
    summaryDefaultEditableLabel: 'Redigera rapport',
    summaryInconsistencyWarningText: 'Värden som påverkar denna rapport har ändrats efter att redigeringen har påbörjats. På grund av detta kommer rapporten återgå till den auto-genererade rapporten. Avsluta redigeringsläget och starta på nytt eller reversera de ändringarna som gjorts för att möjliggöra redigering igen.',
    summaryInconsistencyWarningAllowedText: 'Värden som påverkar denna rapport har ändrats efter att redigeringen har påbörjats. Dessa kommer ej längre automatiskt uppdatera rapporten vilken kan leda till en mismatch mellan rapporten och underliggande strukturerad information.',
    summaryInconsistencyWarningReferencedText: 'Värden som påverkar denna rapport har ändrats på annat ställe vilket kan leda till inkonsekvent information.',
  },
  'nl': {
    defaultAndWord: ', en ',
    mandatoryProhibitFieldsText: 'De volgende verplichte velden zijn leeg:',
    mandatoryAlertFieldsText: 'De volgende aanbevolen velden zijn leeg:',
    checkboxListOtherOptionName: 'Anders',
    checkboxListOtherOptionPlaceholderText: undefined,
    listSectionItemName: 'Nummer {index}',
    listSectionAddButtonText: 'Voeg toe',
    inputNumberRefWarningText: 'De opgegeven waarde bevindt zich niet binnen het referentieinterval',
    inputNumberRangeWarningText: 'De opgegeven waarde is niet toegestaan',
    summaryDefaultLabel: 'Verslag',
    summaryDefaultEditableLabel: 'Bewerk verslag',
    summaryInconsistencyWarningText: 'Waarden die invloed hebben op dit verslag zijn gewijzigd sinds het openen van het verslag in bewerk-modus. Dit leidt tot mogelijke inconsistenties. Als gevolg daarvan zal de tekst worden teruggezet naar de automatisch gegenereerde verslagtekst. Verlaat de bewerkmodus en open deze opnieuw of wijzig de aangepaste waarden om dit ongedaan te maken.',
    summaryInconsistencyWarningAllowedText: 'Waarden die invloed hebben op dit verslag zijn gewijzigd sinds open van het verslag in bewerk-modus. Dit leidt tot mogelijke inconsistenties. Wijzigingen van de waarden zullen niet meer automatisch in het verslag worden bijgewerkt wat zou kunnen leiden tot een mogelijke mismatch tussen het verslag en de onderliggende gestructureerde gegevens.',
    summaryInconsistencyWarningReferencedText: 'Waarden die invloed hebben op dit verslag zijn gewijzigd sinds open van het verslag in bewerk-modus. Dit leidt tot mogelijke inconsistenties.',
  },
  'ge': {
    defaultAndWord: ', und ',
    mandatoryProhibitFieldsText: 'Die folgenden Pflichtfelder sind leer:',
    mandatoryAlertFieldsText: 'Die folgenden empfohlenen Felder sind leer:',
    checkboxListOtherOptionName: 'Sonstiges',
    checkboxListOtherOptionPlaceholderText: undefined,
    listSectionItemName: 'Element {index}',
    listSectionAddButtonText: 'Neues Element hinzufügen',
    inputNumberRefWarningText: 'Der angegebene Wert liegt nicht innerhalb des Referenzintervalls',
    inputNumberRangeWarningText: 'Der angegebene Wert ist nicht zulässig',
    summaryDefaultLabel: 'Bericht',
    summaryDefaultEditableLabel: 'Bericht bearbeiten',
    summaryInconsistencyWarningText: 'Die Informationen, die diesen Bericht betreffen, wurden seit dem Eintritt in den Bearbeitungsmodus geändert, was zu möglichen Inkonsistenzen führt. Infolgedessen wird der resultierende Berichtstext auf den automatisch generierten Berichtstext zurückgesetzt. Verlassen Sie den Bearbeitungsmodus und treten Sie erneut ein, um diesen Zustand zurückzusetzen, oder machen Sie Formularänderungen rückgängig, die zu diesem Zustand geführt haben.',
    summaryInconsistencyWarningAllowedText: 'Die Informationen, die diesen Bericht betreffen, wurden seit dem Eintritt in den Bearbeitungsmodus geändert, was zu möglichen Inkonsistenzen führt. Diese Änderungen werden den Bericht nicht mehr automatisch aktualisieren, was zu einer Diskrepanz zwischen dem Bericht und den zugrunde liegenden strukturierten Informationen führen kann.',
    summaryInconsistencyWarningReferencedText: 'Informationen, die diesen Bericht betreffen, wurden an anderer Stelle geändert, was zu möglichen Unstimmigkeiten führt.',
  },
};

export interface TemplateDefaults extends NonLangDependentTemplateDefaults, LangDependentTemplateDefaults {}

export function setupTemplateDefaults(fromSpec: TemplateDefaults | undefined, langCode: string | undefined): TemplateDefaults {
  const base = langCode != null && langDependentTemplateDefaults[langCode] != null ? langDependentTemplateDefaults[langCode] : langDependentTemplateDefaults.en;
  return { ...baseDefaults, ...base, ...fromSpec } as TemplateDefaults;
}
